import { defineMessage, useIntl } from 'react-intl'
import { ListingStatusEnum } from '../types/listingStatus'

import { useTheme } from '@homebotapp/hb-react-component-catalog'
import statusForSaleLight from '../assets/homes/img/statusForSaleLight.svg'
import statusForSaleDark from '../assets/homes/img/statusForSaleDark.svg'
import statusComingSoonLight from '../assets/homes/img/statusComingSoonLight.svg'
import statusComingSoonDark from '../assets/homes/img/statusComingSoonDark.svg'
import statusPendingLight from '../assets/homes/img/pendingLight.svg'
import statusPendingDark from '../assets/homes/img/statusPendingDark.svg'
import statusClosed from '../assets/homes/img/statusClosed.svg'
import statusHold from '../assets/homes/img/statusHold.svg'
import statusNotAvailable from '../assets/homes/img/statusNotAvailable.svg'
import statusActiveUnderContractLight from '../assets/homes/img/statusActiveUnderContractLight.svg'
import statusActiveUnderContractDark from '../assets/homes/img/statusActiveUnderContractDark.svg'

/**
  Keep this consistent with ListingStatusNew
 */

// Theme-aware icon components
const ForSaleIcon = () => {
  const { theme } = useTheme()
  return <img src={theme.isLightThemeType ? statusForSaleLight : statusForSaleDark} />
}

const ComingSoonIcon = () => {
  const { theme } = useTheme()
  return <img src={theme.isLightThemeType ? statusComingSoonLight : statusComingSoonDark} />
}

const ActiveUnderContractIcon = () => {
  const { theme } = useTheme()
  return <img src={theme.isLightThemeType ? statusActiveUnderContractLight : statusActiveUnderContractDark} />
}

const PendingIcon = () => {
  const { theme } = useTheme()
  return <img src={theme.isLightThemeType ? statusPendingLight : statusPendingDark} />
}

// Generic icon component factory
const createIcon = (src: string, alt: string) => () => <img src={src} alt={alt} />

const ClosedIcon = createIcon(statusClosed, 'Closed')
const HoldIcon = createIcon(statusHold, 'Hold')
const NotAvailableIcon = createIcon(statusNotAvailable, 'Not Available')

const STATUSES = {
  [ListingStatusEnum.ACTIVE]: {
    icon: ForSaleIcon,
    msg: defineMessage({
      id: 'Listings.status.active',
      defaultMessage: 'Active'
    })
  },
  [ListingStatusEnum.ACTIVE_UNDER_CONTRACT]: {
    icon: ActiveUnderContractIcon,
    msg: defineMessage({
      id: 'Listings.status.activeUnderContract',
      defaultMessage: 'Active Under Contract'
    })
  },
  [ListingStatusEnum.CANCELLED]: {
    icon: ClosedIcon,
    msg: defineMessage({
      id: 'Listings.status.cancelled',
      defaultMessage: 'Cancelled'
    })
  },
  [ListingStatusEnum.CLOSED]: {
    icon: ClosedIcon,
    msg: defineMessage({
      id: 'Listings.status.closed',
      defaultMessage: 'Closed'
    })
  },
  [ListingStatusEnum.COMING_SOON]: {
    icon: ComingSoonIcon,
    msg: defineMessage({
      id: 'Listings.status.comingSoon',
      defaultMessage: 'Coming Soon'
    })
  },
  [ListingStatusEnum.DELETE]: {
    icon: ClosedIcon,
    msg: defineMessage({
      id: 'Listings.status.delete',
      defaultMessage: 'Delete'
    })
  },
  [ListingStatusEnum.EXPIRED]: {
    icon: ClosedIcon,
    msg: defineMessage({
      id: 'Listings.status.expired',
      defaultMessage: 'Expired'
    })
  },
  [ListingStatusEnum.HOLD]: {
    icon: HoldIcon,
    msg: defineMessage({
      id: 'Listings.status.hold',
      defaultMessage: 'Hold'
    })
  },
  [ListingStatusEnum.INCOMPLETE]: {
    icon: PendingIcon,
    msg: defineMessage({
      id: 'Listings.status.incomplete',
      defaultMessage: 'Incomplete'
    })
  },
  [ListingStatusEnum.PENDING]: {
    icon: PendingIcon,
    msg: defineMessage({
      id: 'Listings.status.pending',
      defaultMessage: 'Pending'
    })
  },
  [ListingStatusEnum.WITHDRAWN]: {
    icon: ClosedIcon,
    msg: defineMessage({
      id: 'Listings.status.withdrawn',
      defaultMessage: 'Withdrawn'
    })
  },
  [ListingStatusEnum.UNKNOWN]: {
    icon: NotAvailableIcon,
    msg: defineMessage({
      id: 'Listings.status.unknown',
      defaultMessage: 'Unknown'
    })
  }
}

export const useListingStatusDisplayObj = (status: ListingStatusEnum) => {
  const intl = useIntl()
  const statusObj = STATUSES[status] || STATUSES[ListingStatusEnum.UNKNOWN]

  return {
    icon: statusObj.icon,
    text: intl.formatMessage(statusObj.msg)
  }
}
